// Appearance settings: theme (light/dark/system), font family and font size.
// Persisted to localStorage and applied directly to <html> so they take effect
// app-wide (Tailwind dark mode is class-based, and rem-based sizing scales with
// the root font-size).

export const APPEARANCE_KEY = 'appearance'

// Font family options. The chosen stack is written to the --app-font-family CSS
// variable, which the Tailwind `sans` font stack references (see tailwind.config.js),
// so it cascades to the whole app.
// Curated set of popular UI fonts (the kind used by ChatGPT, Perplexity, Linear,
// etc.). The non-system families are loaded from Google Fonts in public/index.html.
// Note: the `sans` key (Inter) is the historical default — keep it so existing
// saved preferences keep working.
export const fontFamilies = {
  sans: {
    label: 'Inter',
    stack: "'Inter var', 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif"
  },
  system: {
    label: 'System',
    stack: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  roboto: {
    label: 'Roboto',
    stack: "'Roboto', ui-sans-serif, system-ui, sans-serif"
  },
  openSans: {
    label: 'Open Sans',
    stack: "'Open Sans', ui-sans-serif, system-ui, sans-serif"
  },
  poppins: {
    label: 'Poppins',
    stack: "'Poppins', ui-sans-serif, system-ui, sans-serif"
  },
  lato: {
    label: 'Lato',
    stack: "'Lato', ui-sans-serif, system-ui, sans-serif"
  },
  nunito: {
    label: 'Nunito',
    stack: "'Nunito', ui-rounded, ui-sans-serif, system-ui, sans-serif"
  },
  merriweather: {
    label: 'Merriweather',
    stack: "'Merriweather', ui-serif, Georgia, Cambria, 'Times New Roman', serif"
  },
  mono: {
    label: 'JetBrains Mono',
    stack: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"
  }
}

// Font size options. `px` is applied to the root element; since the app is
// built with rem-based Tailwind utilities, this scales typography consistently.
export const fontSizes = {
  small: { label: 'Small', px: 14 },
  medium: { label: 'Medium', px: 16 },
  large: { label: 'Large', px: 18 }
}

export const themes = {
  light: { label: 'Light' },
  dark: { label: 'Dark' },
  system: { label: 'System' }
}

// User-customizable colors. Each maps to the CSS custom properties it drives,
// so picking one value recolors the whole surface/border/text family.
export const colorTargets = {
  bg: {
    label: 'Background',
    vars: [
      '--c-bg', '--c-surface', '--c-surface-2', '--c-surface-3', '--c-surface-4',
      '--c-sunken', '--c-hover'
    ]
  },
  border: {
    label: 'Borders',
    vars: ['--c-border', '--c-border-strong']
  },
  text: {
    label: 'Text',
    // Drive the whole text hierarchy so the chosen colour applies to all text.
    vars: ['--c-text', '--c-text-2', '--c-text-muted']
  }
}

export const defaultAppearance = {
  theme: 'light',
  fontFamily: 'sans',
  fontSize: 'medium',
  // Per-mode color overrides, e.g. { light: { bg, border, text }, dark: {...} }.
  customColors: { light: {}, dark: {} }
}

// Which concrete mode is active (resolves "system" against the OS preference).
export function resolveMode(state) {
  return state.theme === 'dark' || (state.theme === 'system' && prefersDark()) ? 'dark' : 'light'
}

export function loadAppearance() {
  try {
    const raw = localStorage.getItem(APPEARANCE_KEY)
    if (!raw) return { ...defaultAppearance }
    const parsed = JSON.parse(raw)
    return {
      ...defaultAppearance,
      ...parsed,
      customColors: {
        light: { ...(parsed.customColors?.light || {}) },
        dark: { ...(parsed.customColors?.dark || {}) }
      }
    }
  } catch {
    return { ...defaultAppearance }
  }
}

export function saveAppearance(state) {
  try {
    localStorage.setItem(
      APPEARANCE_KEY,
      JSON.stringify({
        theme: state.theme,
        fontFamily: state.fontFamily,
        fontSize: state.fontSize,
        customColors: state.customColors
      })
    )
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

function prefersDark() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  )
}

export function applyAppearance(state) {
  if (typeof document === 'undefined') return
  const root = document.documentElement

  // Theme
  const mode = resolveMode(state)
  root.classList.toggle('dark', mode === 'dark')

  // Font family
  const family = fontFamilies[state.fontFamily] || fontFamilies.sans
  root.style.setProperty('--app-font-family', family.stack)

  // Font size (scales all rem-based sizing)
  const size = fontSizes[state.fontSize] || fontSizes.medium
  root.style.fontSize = `${size.px}px`

  // Custom colors for the active mode (inline overrides the stylesheet tokens;
  // cleared vars fall back to the built-in light/dark defaults).
  const colors = (state.customColors && state.customColors[mode]) || {}
  let hasCustom = false
  for (const [key, target] of Object.entries(colorTargets)) {
    const value = colors[key]
    if (value) hasCustom = true
    for (const cssVar of target.vars) {
      if (value) root.style.setProperty(cssVar, value)
      else root.style.removeProperty(cssVar)
    }
  }

  // `theme-custom` activates the neutral utility remaps in LIGHT mode too, so a
  // user's custom colors apply app-wide (in dark, html.dark already covers it).
  root.classList.toggle('theme-custom', hasCustom)
}

// Re-apply when the OS theme changes while the user is on "system".
export function watchSystemTheme(getState) {
  if (typeof window === 'undefined' || !window.matchMedia) return
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  const handler = () => {
    const state = getState()
    if (state.theme === 'system') applyAppearance(state)
  }
  if (mq.addEventListener) mq.addEventListener('change', handler)
  else if (mq.addListener) mq.addListener(handler)
}
